import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useStoreInfo } from "../../context/StoreInfoContext";
import { AppTypography } from "../common/Typography";
import AppButton from "../common/Button";
import SupportPopup from "../common/SupportPopup";
import { useTheme } from "../../theme/ThemeContext";
import { getCart } from "../../utils/cart";
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  MenuItem,
  Avatar,
  Divider,
} from "@mui/material";
import {
  ShoppingCart as CartIcon,
  Person as PersonIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import styled from "@emotion/styled";
import { usePreserveStoreIdNavigate } from "../utils/usePreserveStoreIdNavigate";

const Navbar = () => {
  const theme = useTheme();
  const navigate = usePreserveStoreIdNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { storeInfo } = useStoreInfo();
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isStorePublic, setIsStorePublic] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(0);
  const profileIconRef = useRef(null);
  const dropdownRef = useRef(null);
  const [supportOpen, setSupportOpen] = useState(false);

  // Check if we're on the login page
  const isLoginPage = location.pathname === "/login";
  // Cart dropdown minimal state

  // Check if login is required and if user is authenticated
  useEffect(() => {
    setIsStorePublic(storeInfo?.store?.is_public === 1);
  }, [storeInfo]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !profileIconRef.current?.contains(event.target)
      ) {
        setAnchorEl(null);
      }
    };

    if (anchorEl) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [anchorEl]);

  // Get cart count from localStorage
  useEffect(() => {
    const updateCartCount = () => {
      const cart = getCart();
      const count = cart?.length || 0;
      // cart?.reduce(
      //   (total, item) => total + item.selected_quantity,
      //   0
      // );
      setCartItemCount(count);
    };

    // Update count on mount
    updateCartCount();

    // Listen for storage changes (when cart is updated in other tabs)
    const handleStorageChange = (e) => {
      if (e.key === "swagstore_cart") {
        updateCartCount();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Listen for custom cart update events
    window.addEventListener("cartUpdated", updateCartCount);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("cartUpdated", updateCartCount);
    };
  }, []);

  const handleMenuClick = (event) => {
    // Toggle dropdown - if open, close it; if closed, open it
    setAnchorEl(anchorEl ? null : profileIconRef.current);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSupportOpen = () => setSupportOpen(true);
  const handleSupportClose = () => setSupportOpen(false);

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
    handleMenuClose();
  };

  // Theme-based styles
  const StyledAppBar = styled(AppBar)({
    backgroundColor: theme?.navbar_background_color || theme?.card_bg_color,
    color: theme?.text_color,
    boxShadow: theme?.navbar_shadow,
    position: "sticky",
    top: 0,
    zIndex: 1000,
  });

  // Add global styles to hide scrollbar
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      /* Hide scrollbar for Chrome, Safari and Opera */
      ::-webkit-scrollbar {
        display: none;
      }
      
      /* Hide scrollbar for IE, Edge and Firefox */
      html {
        -ms-overflow-style: none;  /* IE and Edge */
        scrollbar-width: none;  /* Firefox */
      }
      
      body {
        -ms-overflow-style: none;  /* IE and Edge */
        scrollbar-width: none;  /* Firefox */
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const Logo = styled.img({
    padding: 4,
    height: "40px",
    cursor: "pointer",
  });

  return (
    <>
      <StyledAppBar>
        <Toolbar sx={{ justifyContent: "space-between", px: { xs: 2, md: 4 } }}>
          {/* Logo */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Logo
              src={theme?.company_logo}
              alt={theme?.store_name}
              onClick={() => handleNavigation("/")}
            />
          </Box>

          {/* Desktop Navigation */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: 2,
            }}
          >
            <AppButton
              variant="text"
              onClick={() => handleNavigation("/")}
              sx={{
                backgroundColor: theme?.card_bg_color,
                color: theme?.text_color,
                fontWeight: 500,
                fontSize: "0.9rem",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                },
              }}
            >
              Home
            </AppButton>

            {/* Cart Icon - Show for public stores always, private stores only when logged in */}
            {(isStorePublic || (!isStorePublic && user)) && (
              <Box
                sx={{ position: "relative", mx: 1, display: "inline-block" }}
              >
                <IconButton
                  sx={{
                    backgroundColor: theme?.cart_color,
                    fontWeight: 500,
                    fontSize: "0.9rem",
                  }}
                  onClick={() => handleNavigation("/cart")}
                >
                  <Badge badgeContent={cartItemCount} color="error">
                    <CartIcon />
                  </Badge>
                </IconButton>
              </Box>
            )}

            {/* Profile avatar + user info */}
            <Box
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <IconButton
                ref={profileIconRef}
                onClick={handleMenuClick}
                sx={{ backgroundColor: theme?.menu_color }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: theme?.app_button_color,
                  }}
                >
                  {user ? (
                    <AppTypography
                      variant="body2"
                      sx={{ color: "white", fontWeight: 600 }}
                    >
                      {user.first_name?.charAt(0)?.toUpperCase() ||
                        user.name?.charAt(0)?.toUpperCase() ||
                        user.email?.charAt(0)?.toUpperCase() ||
                        "U"}
                    </AppTypography>
                  ) : (
                    <PersonIcon fontSize="small" />
                  )}
                </Avatar>
              </IconButton>

              {user && (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <AppTypography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      color: theme?.text_color,
                      lineHeight: 1.1,
                    }}
                  >
                    {user.first_name && user.last_name
                      ? `${user.first_name} ${user.last_name}`
                      : user.first_name ||
                        user.name ||
                        user.email?.split("@")[0]}
                  </AppTypography>
                  {user.email && (
                    <AppTypography
                      variant="body2"
                      sx={{
                        fontSize: "0.75rem",
                        color: theme?.app_grey,
                        lineHeight: 1.1,
                      }}
                    >
                      {user.email}
                    </AppTypography>
                  )}
                </Box>
              )}

              {/* Custom Dropdown Menu */}
              {Boolean(anchorEl) && (
                <Box
                  ref={dropdownRef}
                  sx={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    mt: 1,
                    backgroundColor: "white",
                    borderRadius: 2,
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                    minWidth: 150,
                    zIndex: 1000,
                    border: "1px solid #e0e0e0",
                  }}
                >
                  {user ? (
                    <>
                      {/* <MenuItem>
                        <AppTypography
                          variant="body2"
                          sx={{ fontWeight: 500, fontSize: "0.9rem" }}
                        >
                          Welcome, {user.first_name}
                        </AppTypography>
                      </MenuItem> */}
                      <MenuItem
                        onClick={() => {
                          handleNavigation("/cart");
                          setAnchorEl(null);
                        }}
                      >
                        <AppTypography
                          variant="body2"
                          sx={{ fontWeight: 500, fontSize: "0.9rem" }}
                        >
                          Cart
                        </AppTypography>
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          handleNavigation("/my-orders");
                          setAnchorEl(null);
                        }}
                      >
                        <AppTypography
                          variant="body2"
                          sx={{ fontWeight: 500, fontSize: "0.9rem" }}
                        >
                          My Orders
                        </AppTypography>
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          handleNavigation("/my-addresses");
                          setAnchorEl(null);
                        }}
                      >
                        <AppTypography
                          variant="body2"
                          sx={{ fontWeight: 500, fontSize: "0.9rem" }}
                        >
                          My Addresses
                        </AppTypography>
                      </MenuItem>
                      {user?.id ? (
                        <MenuItem
                          onClick={() => {
                            setAnchorEl(null);
                            handleSupportOpen();
                          }}
                        >
                          <AppTypography
                            variant="body2"
                            sx={{ fontWeight: 500, fontSize: "0.9rem" }}
                          >
                            Contact Support
                          </AppTypography>
                        </MenuItem>
                      ) : null}

                      <MenuItem
                        onClick={() => {
                          logout();
                          setAnchorEl(null);
                          navigate("/");
                        }}
                        sx={{ marginBottom: 1 }}
                      >
                        <AppTypography
                          variant="body1"
                          fontWeight={500}
                          sx={{ fontSize: "0.9rem" }}
                        >
                          Logout
                        </AppTypography>
                      </MenuItem>
                    </>
                  ) : (
                    !isLoginPage && (
                      <MenuItem
                        onClick={() => {
                          handleNavigation("/login");
                          setAnchorEl(null);
                        }}
                        sx={{ marginY: 1 }}
                      >
                        <AppTypography
                          variant="body2"
                          sx={{ fontWeight: 500, fontSize: "0.9rem" }}
                        >
                          Login
                        </AppTypography>
                      </MenuItem>
                    )
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {/* Mobile Menu Button */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              gap: 1,
            }}
          >
            {/* Cart Icon - Show for public stores always, private stores only when logged in */}

            <IconButton
              onClick={() => handleNavigation("/cart")}
              sx={{ backgroundColor: theme?.cart_color }}
            >
              <Badge badgeContent={cartItemCount} color="error">
                <CartIcon />
              </Badge>
            </IconButton>

            <IconButton
              onClick={handleMobileMenuToggle}
              sx={{ backgroundColor: theme?.menu_color }}
            >
              {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          </Box>
        </Toolbar>

        {/* Mobile Menu */}
        <Box
          sx={{
            display: { xs: mobileMenuOpen ? "block" : "none", md: "none" },
            backgroundColor: "#ffffff",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <Box sx={{ py: 2, px: 2 }}>
            <MenuItem onClick={() => handleNavigation("/")} sx={{ py: 1.5 }}>
              <AppTypography variant="body1" fontWeight={600}>
                Home
              </AppTypography>
            </MenuItem>

            <Divider sx={{ my: 1 }} />
            <MenuItem
              onClick={() => handleNavigation("/cart")}
              sx={{ py: 1.5 }}
            >
              <AppTypography variant="body1" fontWeight={500}>
                Cart
              </AppTypography>
            </MenuItem>

            {/* Hide user menu if store is public */}
            {user ? (
              <>
                <MenuItem
                  onClick={() => {
                    handleNavigation("/my-orders");
                    setMobileMenuOpen(false);
                  }}
                  sx={{ py: 1.5 }}
                >
                  <AppTypography variant="body1" fontWeight={500}>
                    My Orders
                  </AppTypography>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleNavigation("/my-addresses");
                    setMobileMenuOpen(false);
                  }}
                  sx={{ py: 1.5 }}
                >
                  <AppTypography variant="body1" fontWeight={500}>
                    My Addresses
                  </AppTypography>
                </MenuItem>
                {user?.id ? (
                  <MenuItem
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleSupportOpen();
                    }}
                    sx={{ py: 1.5 }}
                  >
                    <AppTypography variant="body1" fontWeight={500}>
                      Contact Support
                    </AppTypography>
                  </MenuItem>
                ) : null}
                <Divider sx={{ my: 1 }} />
                <MenuItem
                  onClick={() => {
                    logout();
                    setMobileMenuOpen(false);
                    navigate("/");
                  }}
                  sx={{ py: 1 }}
                >
                  <AppTypography variant="body1" fontWeight={500}>
                    Logout
                  </AppTypography>
                </MenuItem>
              </>
            ) : null}
            {/* Hide login/register if store is public or if on login page */}
            {!user && !isLoginPage ? (
              <>
                <MenuItem
                  onClick={() => handleNavigation("/login")}
                  sx={{ py: 1.5 }}
                >
                  <AppTypography variant="body1" fontWeight={500}>
                    Login
                  </AppTypography>
                </MenuItem>
              </>
            ) : null}
          </Box>
        </Box>
      </StyledAppBar>
      <SupportPopup open={supportOpen} onClose={handleSupportClose} />
    </>
  );
};

export default Navbar;
